import { HttpClient } from '@adapters/HttpClient';
import type {
  ActivateProductBatchesRequest,
  ActivateProductBatchesResponse,
  Batch,
  BatchesApiResponse,
  BatchFilters,
  BatchPage,
  BatchStatusCounts,
  BatchStock,
  BatchTraceabilityEvent,
  BlockBatchRequest,
  CreateBatchRequest,
  UpdateBatchRequest,
} from '../models/Batch';

export class BatchApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getBatchesPaginated(filters: BatchFilters = {}): Promise<BatchPage> {
    const { status, sortBy, sortDirection, page = 1, limit = 25, advanced_filters } = filters;

    const params: Record<string, any> = {
      page,
      per_page: limit,
    };

    if (status) params.status = status;
    if (sortBy) params['sorting[column]'] = sortBy;
    if (sortDirection) params['sorting[direction]'] = sortDirection;

    if (advanced_filters?.filters) {
      const voidOperators = new Set(['is_null', 'is_not_null']);

      advanced_filters.filters.forEach((filter, index) => {
        params[`advanced_filters[filters][${index}][column]`] = filter.column;
        params[`advanced_filters[filters][${index}][operator]`] = filter.operator;

        if (voidOperators.has(filter.operator)) {
          return;
        }

        if (filter.operator === 'in' || filter.operator === 'not_in') {
          if (Array.isArray(filter.value)) {
            filter.value.forEach((val: any, valIndex: number) => {
              params[`advanced_filters[filters][${index}][value][${valIndex}]`] = val;
            });
          } else {
            params[`advanced_filters[filters][${index}][value][0]`] = filter.value;
          }
        } else if (filter.value !== undefined) {
          params[`advanced_filters[filters][${index}][value]`] = filter.value;
        }
      });
      params['advanced_filters[logic]'] = advanced_filters.logic || 'AND';
    }

    const showGlobalLoading = page === 1;

    const response = await this.httpClient.get<BatchesApiResponse>('/batches', params, {}, showGlobalLoading);

    // TODO: Request backend to include product relation in /batches response
    return {
      batches: response.data,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  async getBatch(batchId: number): Promise<Batch> {
    return this.httpClient.get<Batch>(`/batches/${batchId}`);
  }

  async createBatch(data: CreateBatchRequest): Promise<{ id: number }> {
    return this.httpClient.post<{ id: number }>('/batches', data);
  }

  async updateBatch(batchId: number, data: UpdateBatchRequest): Promise<Batch> {
    return this.httpClient.put<Batch>(`/batches/${batchId}`, data);
  }

  async deleteBatch(batchId: number): Promise<void> {
    await this.httpClient.delete(`/batches/${batchId}`);
  }

  async getBatchStock(batchId: number, includeZero = false): Promise<BatchStock[]> {
    const params: Record<string, any> = {};
    if (includeZero) params.include_zero = true;
    return this.httpClient.get<BatchStock[]>(`/batches/${batchId}/stock`, params);
  }

  async getBatchTraceability(batchId: number): Promise<BatchTraceabilityEvent[]> {
    return this.httpClient.get<BatchTraceabilityEvent[]>(`/batches/${batchId}/traceability`);
  }

  async blockBatch(batchId: number, data?: BlockBatchRequest): Promise<Batch> {
    return this.httpClient.post<Batch>(`/batches/${batchId}/block`, data || {});
  }

  async unblockBatch(batchId: number): Promise<Batch> {
    return this.httpClient.post<Batch>(`/batches/${batchId}/unblock`, {});
  }

  async getProductBatches(productId: number): Promise<Batch[]> {
    return this.httpClient.get<Batch[]>(`/products/${productId}/batches`);
  }

  async activateProductBatches(
    productId: number,
    data: ActivateProductBatchesRequest,
  ): Promise<ActivateProductBatchesResponse> {
    return this.httpClient.post<ActivateProductBatchesResponse>(`/products/${productId}/batches/activate`, data);
  }

  async getStatusSummary(): Promise<BatchStatusCounts> {
    const data = await this.httpClient.get<Omit<BatchStatusCounts, 'all'>>('/batches/status-summary', {}, {}, false);
    return {
      all: data.active + data.blocked + data.expired + data.depleted,
      ...data,
    };
  }
}
